import type { JSX } from 'react';
import { memo, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Pagination from '@mui/material/Pagination';
import { gridPageCountSelector, gridPaginationModelSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PaginationMode } from 'common/constants/paginationMode';

import { selectPaginationMode, setPaginationModel } from './palmDataGridSlice';

const CustomPagination = memo((): JSX.Element => {
    const apiRef = useGridApiContext();
    const dispatch = useAppDispatch();
    const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    const paginationMode = useAppSelector(selectPaginationMode) ?? PaginationMode.Client;
    const location = useLocation();

    const resetPaginationToFirstPage = useCallback(() => {
        if (paginationMode === PaginationMode.Client) {
            if (apiRef.current) {
                apiRef.current.setPage(0);
            }
        } else {
            dispatch(setPaginationModel({
                page: 0,
                pageSize: paginationModel.pageSize
            }));
        }
    }, [paginationMode, apiRef, dispatch, paginationModel.pageSize]);

    useEffect(() => {
        resetPaginationToFirstPage();
    }, [location.pathname, location.search, location.hash, resetPaginationToFirstPage]);

    useEffect(() => {
        if (pageCount > 0 && paginationModel.page >= pageCount) {
            resetPaginationToFirstPage();
        }
    }, [pageCount, paginationModel.page, resetPaginationToFirstPage]);

    const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => {
        apiRef.current.setPage(value - 1);
    }, [apiRef]);

    return (
        <Pagination
            sx={{
                justifyContent: 'center',
                margin: 'auto',
                '& .Mui-selected': {
                    backgroundColor: (theme) => `${theme.palette.primary.accent} !important`,
                    color: 'common.white',
                },
            }}
            color="primary"
            count={pageCount}
            page={paginationModel.page + 1}
            onChange={handlePageChange}
            showFirstButton={true}
            showLastButton={true}
        />
    );
});

CustomPagination.displayName = 'CustomPagination';

export { CustomPagination };
